import CopyButton from '@/components/copy-button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useEmailGeneration, useEmailResults } from '@/hooks/use-emailHealth'
import { usePermission } from '@/hooks/use-premission'
import { USAGE_QUERY_KEY, useGetUsage } from '@/hooks/use-subscription'
import { useUsageLimitChecker } from '@/hooks/use-usage-limit-checker'
import { RESTRICTED_DOMAINS } from '@/lib/constants'
import { Warning } from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { CheckCircle, Clock, Copy, Info, Mail, X, XCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

const TEST_EMAIL_ADDRESS = 'health-test@yourdomain.com'

const TIMER_MILLI_SECONDS = 15000

export const Route = createFileRoute(
  '/_private/domain-health/email-health-test',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const {
    testEmail,
    isGenerating,
    generateNewTestEmail,
    reset: resetTestEmail,
  } = useEmailGeneration()
  const [hasMailSent, setHasMailSent] = useState(false)
  const { healthResults, isLoadingResults, hasError } = useEmailResults(
    testEmail,
    hasMailSent,
  )
  const [countdown, setCountdown] = useState(15)
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isFreeDomainDialogOpen, setIsFreeDomainDialogOpen] = useState(false)

  const { data: usageData } = useGetUsage()
  const queryClient = useQueryClient()
  const { hasPermission } = usePermission()
  const { isUsageLimitReached } = useUsageLimitChecker()

  // Define the steps with icons and labels
  const checkSteps = [
    { label: 'Email Authentication', status: 'pending' },
    { label: 'DNS Configuration Analysis', status: 'pending' },
    { label: 'Mail Server Infrastructure', status: 'pending' },
    { label: 'TLS/SSL Security Protocol', status: 'pending' },
    { label: 'IP Reputation & Blacklist Scan', status: 'pending' },
    { label: 'Verifying Details', status: 'pending' },
  ]

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (hasMailSent && testEmail && !healthResults) {
      // Update steps every 15 seconds (to complete in ~90 seconds)
      timer = setInterval(() => {
        setCurrentStep((prev) => {
          // Keep the last step showing until results arrive
          if (prev >= checkSteps.length - 1) return checkSteps.length - 1
          return prev + 1
        })
      }, TIMER_MILLI_SECONDS)

      // Start with first step immediately
      setCurrentStep(0)
    }
    return () => clearInterval(timer)
  }, [hasMailSent, testEmail, healthResults, checkSteps.length])

  const handleGenerateNewTestEmail = () => {
    try {
      if (isUsageLimitReached('limit_healthTestsUsedThisMonth')) {
        toast.error(
          'You have reached the limit of email health tests for this month',
        )
        return
      }

      generateNewTestEmail()
      setHasMailSent(false)
    } catch (error) {
      toast.error('Failed to generate new test email')
    }
  }

  const handleMailSent = () => {
    setHasMailSent(true)
    toast.success('Starting to check for email results')
    window.cioanalytics?.track('email_health_test_run_unconnected', {})
  }

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pass':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'fail':
        return <XCircle className="h-5 w-5 text-red-500" />
      default:
        return <Clock className="h-5 w-5 text-yellow-500" />
    }
  }

  // if(hasMailSent && testEmail && !healthResults){
  //   return <div className='flex justify-center items-center h-[80vh] w-full '>
  //     <div className="newtons-cradle">
  //       <div className="newtons-cradle__dot"></div>
  //       <div className="newtons-cradle__dot"></div>
  //       <div className="newtons-cradle__dot"></div>
  //       <div className="newtons-cradle__dot"></div>
  //     </div>
  //   </div>
  // }

  const handleReset = () => {
    setIsFreeDomainDialogOpen(false)
    setIsDialogOpen(false)
    setHasMailSent(false)
    setCurrentStep(0)
    resetTestEmail()
    setHasMailSent(false)
  }

  useEffect(() => {
    if (hasMailSent && testEmail && !healthResults) {
      setIsDialogOpen(true)
    }

    if (hasMailSent && testEmail && healthResults) {
      setHasMailSent(false)
    }

    if (
      hasMailSent &&
      testEmail &&
      healthResults &&
      RESTRICTED_DOMAINS.includes(healthResults?.domain)
    ) {
      setIsFreeDomainDialogOpen(true)
      setIsDialogOpen(false)
    }
  }, [hasMailSent, testEmail, healthResults])

  return (
    <div className="w-full">
      {/* header */}

      <div className="flex flex-col lg:flex-row gap-2 mb-4 w-full justify-between">
        <div className="flex flex-col">
          <h1 className="page-heading">Test Your Email Deliverability</h1>
          <p className="text-muted-foreground text-sm lg:text-base">
            Send a test email to check SPF, DKIM, DMARC, and more.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="text-sm font-medium">
            Available Email Health Tests:
          </div>
          <div className="text-sm text-muted-foreground">
            {(usageData?.maxHealthTestsUsedThisMonth || 0) -
              (usageData?.healthTestsUsedThisMonth || 0)}
          </div>
        </div>
      </div>
      <WarningDialog
        isOpen={isFreeDomainDialogOpen}
        onOpenChange={(open) => {
          setIsFreeDomainDialogOpen(open)
          if (!open) {
            handleReset()
          }
        }}
        onRetry={handleReset}
      />

      {hasMailSent && testEmail && !healthResults && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
            showCloseButton={false}
            className="bg-white p-8 rounded-lg shadow-lg flex flex-col items-center gap-6 max-w-md w-full"
          >
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="absolute right-4 top-4 h-auto w-auto p-2"
                >
                  <X />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <div className="flex flex-col items-center gap-4">
                  <h2 className="text-xl font-bold text-center">
                    Are you sure you want to close?
                  </h2>
                  <p className="text-sm text-muted-foreground text-center">
                    Please wait for 2 minutes. Results will be shown in the grid
                    table. The analysis is still running in the background.
                  </p>
                </div>

                <AlertDialogFooter>
                  <AlertDialogCancel>Continue Waiting</AlertDialogCancel>
                  <AlertDialogAction onClick={() => setIsDialogOpen(false)}>
                    Close Anyway
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <h2 className="text-xl font-bold text-center">
              Analyzing Email Health
            </h2>

            <div className="w-full space-y-4">
              {checkSteps.map((step, index) => {
                // Determine the status of each step
                let status = 'pending'
                if (index < currentStep) status = 'completed'
                else if (index === currentStep) status = 'checking'

                return (
                  <div key={index} className="flex items-center gap-3">
                    <div
                      className={`rounded-full w-6 h-6 flex items-center justify-center ${
                        status === 'completed'
                          ? 'bg-green-500 text-white'
                          : status === 'checking'
                            ? 'border-2 border-blue-500'
                            : 'border-2 border-gray-300'
                      }`}
                    >
                      {status === 'completed' ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : status === 'checking' ? (
                        <div className="h-4 w-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
                      ) : null}
                    </div>

                    <span
                      className={`flex-1 font-medium ${
                        status === 'checking'
                          ? 'text-blue-600'
                          : status === 'completed'
                            ? 'text-gray-700'
                            : 'text-muted-foreground'
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                )
              })}
            </div>

            <p className="text-sm text-blue-600 text-center mt-2">
              {currentStep < checkSteps.length - 1
                ? `${checkSteps[currentStep].label}...`
                : 'Analyzing email health and deliverability...'}
            </p>

            <CountdownTimer
              initialMilliseconds={TIMER_MILLI_SECONDS}
              currentCount={currentStep}
              onComplete={() => {}}
            />
          </DialogContent>
        </Dialog>
      )}

      <Card>
        <CardContent className="p-4">
          <div className="mb-6 p-4 bg-muted rounded-lg flex flex-col gap-2">
            {testEmail && (
              <p className="font-semibold">Send your test email to:</p>
            )}
            <div className="flex lg:items-center justify-between flex-col lg:flex-row gap-2">
              <div className="">
                {testEmail ? (
                  <>
                    <div className="flex flex-col md:flex-row md:items-center gap-2">
                      <div className="border rounded-md px-3 py-1 min-h-8 bg-background dark:bg-transparent flex items-center gap-2">
                        <Mail className="h-4 w-4 shrink-0" />
                        <p className="text-sm lg:text-base font-medium px-1 break-all">
                          {testEmail}
                        </p>
                      </div>
                      <CopyButton
                        text={testEmail}
                        indicator="inline"
                        variant="outline"
                        size="sm"
                        className="text-xs"
                      />
                    </div>
                  </>
                ) : (
                  <p className="font-semibold">
                    Generate an email address to begin
                  </p>
                )}
              </div>
              {!healthResults ? (
                <div className="flex flex-col sm:flex-row w-full sm:w-fit gap-2">
                  {hasPermission('email_health:run_test') && !hasMailSent && (
                    <Button
                      onClick={handleGenerateNewTestEmail}
                      disabled={isGenerating}
                      variant={testEmail ? 'outline' : 'default'}
                    >
                      <Mail className="h-4 w-4" />
                      {isGenerating ? 'Generating...' : 'Generate Email'}
                    </Button>
                  )}
                  {testEmail && (
                    <>
                      <Button
                        onClick={handleMailSent}
                        disabled={hasMailSent || isLoadingResults}
                        variant="default"
                      >
                        <CheckCircle className="h-4 w-4" />
                        {hasMailSent
                          ? 'Checking Results...'
                          : "I've Sent the Mail"}
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button onClick={handleReset}>Run Another Test</Button>
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          {testEmail && !healthResults && (
            <div className="mt-4">
              <h3 className="text-base lg:text-lg font-semibold mb-3">
                Email Health Results
              </h3>
              {isLoadingResults && !healthResults && hasMailSent && (
                <Alert className="mb-4" variant="info">
                  <Clock className="h-4 w-4" />
                  <AlertTitle>Checking for Results</AlertTitle>
                  <AlertDescription>
                    We're checking for your email results. This may take a few
                    minutes.
                  </AlertDescription>
                </Alert>
              )}
              {!hasMailSent && (
                <Alert variant="info">
                  <Clock className="h-4 w-4" />
                  <AlertTitle>Waiting for Your Email</AlertTitle>
                  <AlertDescription>
                    Please send an email to the address above and click "I've
                    Sent the Mail" button.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Show instructions only if email hasn't been sent yet */}
          {!hasMailSent && (
            <div className="mt-4">
              <h3 className="text-base lg:text-lg font-semibold mb-3">
                How to Run the Test
              </h3>
              <ol className="list-decimal pl-4 text-sm space-y-2 mb-6 text-muted-foreground">
                <li>
                  Click the "Generate Email" button and copy the generated
                  address.
                </li>

                <li>
                  Open the email account you want to test and compose a new
                  email.
                </li>

                <li>Paste the copied address into the recipient field.</li>

                <li>
                  Enter any subject and message content — it's completely up to
                  you.
                </li>

                <li>Send the email.</li>

                <li>
                  Return to the app and click the "I've Sent the Mail" button
                  (this button appears after you generate the email).
                </li>

                <li>
                  Wait a few minutes while we process your test and generate the
                  report.
                </li>
              </ol>
            </div>
          )}

          {/* <Alert variant="info" className="mb-6">
            <Info className="h-4 w-4" />
            <AlertTitle>Credit Usage Information</AlertTitle>
            <AlertDescription>
              <span className="font-bold">5 credits</span> will be deducted from
              your account when we receive and process your test email.
            </AlertDescription>
          </Alert> */}
        </CardContent>
        <CardFooter className="flex flex-col items-start p-4 pt-0">
          <h2 className="text-base lg:text-lg font-semibold mb-2">
            Viewing Your Results
          </h2>
          <p className="text-muted-foreground text-sm mb-4">
            After sending your email, please allow a few minutes for processing.
          </p>

          {healthResults &&
            !RESTRICTED_DOMAINS.includes(healthResults.domain) && (
              <Button
                onClick={() => {
                  navigate({
                    to: `/domain-health/${healthResults.domain}`,
                  })

                  queryClient.invalidateQueries({
                    queryKey: [USAGE_QUERY_KEY],
                  })
                }}
              >
                View Detailed Report
              </Button>
            )}
        </CardFooter>
      </Card>
    </div>
  )
}

function CountdownTimer({
  initialMilliseconds = 5000,
  currentCount, // Add this prop to track count changes
  onComplete,
}: {
  initialMilliseconds: number
  currentCount: number
  onComplete: () => void
}) {
  const [milliseconds, setMilliseconds] = useState(initialMilliseconds)
  const [isActive, setIsActive] = useState(true)

  // Reset timer when currentCount changes
  useEffect(() => {
    setMilliseconds(initialMilliseconds)
    setIsActive(true)
  }, [currentCount, initialMilliseconds])

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null

    if (isActive && milliseconds > 0) {
      interval = setInterval(() => {
        setMilliseconds((ms) => ms - 100)
      }, 100)
    } else if (milliseconds <= 0) {
      setIsActive(false)
      if (onComplete) onComplete()
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isActive, milliseconds, onComplete])

  const formatTime = () => {
    const seconds = Math.floor(milliseconds / 1000)
    const ms = milliseconds % 1000
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60

    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`
  }

  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Clock className="h-4 w-4" />
      <span>Estimated Remaining Time: {formatTime()}</span>
    </div>
  )
}

const WarningDialog = ({
  isOpen,
  onOpenChange,
  onRetry,
}: {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onRetry: () => void
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-center">
            <div className="flex justify-center items-center py-4">
              <Warning className="w-10 h-10 text-red-500" />
            </div>
            <h3 className="text-2xl font-semibold max-w-sm mx-auto mb-3">
              Oops! Please Use a Work Email to Run the Test
            </h3>
          </DialogTitle>
          <DialogDescription className="max-w-2xl mx-auto text-center">
            We detected that you sent the test email from an account that
            isn&apos;t your work email (e.g., not yourname@company.com). The
            Email Domain Health Test requires a work email to accurately analyze
            your domain&apos;s configuration and deliverability. Please resend
            the email using your work email to continue..
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-center">
          <Button className="w-fit" onClick={onRetry}>
            <span>Retry with Work Email</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
